import { type ReactNode } from "react";
import {
  DenseTable,
  StatusBadge,
  estimateSuffix,
  defaultNumberFormat,
  type Column,
} from "../../components/index.ts";
import type { IdentityMcpSummary } from "./use-identity-mcp.ts";
import { quantityMeasure, toolLabel, resultLabel } from "./vocab.ts";

/**
 * Operations by tool and outcome (task 7.3; requirement 13.3 "operations by tool and outcome").
 *
 * The summary's `operations` is the per-(tool, outcome) count, zero-filled over the catalog
 * `identity_mcp_tool` × `result` members, so every tool shows both its success and failure rows even
 * when one is zero — "nothing matched" reads differently from "no data" (FR-4.3). The table pivots
 * those rows to one row per tool with a Success column and a Failure column (and a total), which is
 * how an operator reads tool behaviour at a glance. Each cell is a `Quantity` labelled with its
 * exact/estimate confidence; a null count is an em dash styled unverifiable, never a fabricated zero
 * (design 12.3). A failure outcome carries a warn `StatusBadge` on the row when any failures were
 * seen, so a tool denying operations stands out beyond colour (design 12.4).
 */

type OperationStat = IdentityMcpSummary["operations"][number];
type Quantity = OperationStat["count"];

interface ToolRow {
  tool: OperationStat["tool"];
  success: Quantity | null;
  failure: Quantity | null;
}

export function OperationsTable(props: { operations: readonly OperationStat[] }): ReactNode {
  const rows = pivotByTool(props.operations);
  // Busiest first (by total of success + failure); a tool with no data sorts last.
  const sorted = [...rows].sort((a, b) => rowTotal(b) - rowTotal(a));

  const columns: readonly Column<ToolRow>[] = [
    {
      key: "tool",
      header: "Tool",
      isRowHeader: true,
      cell: (r) => (
        <span className="imcp-ops__key">
          {hasFailures(r) && (
            <StatusBadge
              level="warn"
              label="Failures"
              size="sm"
              title={`${toolLabel(r.tool)} had one or more failed operations in this window.`}
            />
          )}
          <span className="imcp-ops__tool">{toolLabel(r.tool)}</span>
        </span>
      ),
    },
    quantityColumn("success", resultLabel("success")),
    quantityColumn("failure", resultLabel("failure")),
    totalColumn(),
  ];

  return (
    <section className="imcp-ops" aria-label="Operations by tool and outcome">
      <h2 className="imcp-section__title">Operations by tool and outcome</h2>
      <DenseTable
        caption="Identity MCP operations broken down by tool and outcome"
        columns={columns}
        rows={sorted}
        rowKey={(r) => r.tool}
        stickyHeader
        emptyMessage="No Identity MCP operations were recorded in this window."
      />
    </section>
  );
}

/** Fold the flat (tool, outcome) stats into one row per tool with a success and a failure cell. */
function pivotByTool(operations: readonly OperationStat[]): ToolRow[] {
  const byTool = new Map<OperationStat["tool"], ToolRow>();
  for (const op of operations) {
    const existing = byTool.get(op.tool) ?? { tool: op.tool, success: null, failure: null };
    if (op.outcome === "success") existing.success = op.count;
    else existing.failure = op.count;
    byTool.set(op.tool, existing);
  }
  return [...byTool.values()];
}

/** True when the row's failure count is a real, non-zero number. */
function hasFailures(r: ToolRow): boolean {
  return r.failure !== null && r.failure.value !== null && r.failure.value > 0;
}

/** The numeric success+failure total for sorting; a row with no data sorts to the bottom. */
function rowTotal(r: ToolRow): number {
  const s = r.success?.value ?? 0;
  const f = r.failure?.value ?? 0;
  if (r.success?.value == null && r.failure?.value == null) return -1;
  return s + f;
}

/** A right-aligned `Quantity` column (success/failure) with its exact/estimate confidence. */
function quantityColumn(key: "success" | "failure", header: string): Column<ToolRow> {
  return {
    key,
    header,
    align: "right",
    cell: (r) => {
      const q = r[key];
      if (q === null) {
        return (
          <span className="imcp-num" data-measure="unverifiable" title={`${header}: no data`}>
            n/a
          </span>
        );
      }
      const measure = quantityMeasure(q);
      const text = q.value === null ? "n/a" : `${defaultNumberFormat(q.value)}${estimateSuffix(measure)}`;
      return (
        <span className="imcp-num" data-measure={measure} title={`${header} for ${toolLabel(r.tool)}`}>
          {text}
        </span>
      );
    },
  };
}

/** The derived success+failure total column; shown but not a drill target (it is derived). */
function totalColumn(): Column<ToolRow> {
  return {
    key: "total",
    header: "Total",
    align: "right",
    cell: (r) => {
      const s = r.success?.value;
      const f = r.failure?.value;
      if (s == null && f == null) {
        return (
          <span className="imcp-num" data-measure="unverifiable">
            n/a
          </span>
        );
      }
      const total = (s ?? 0) + (f ?? 0);
      // The total is an estimate if either contributing count was estimated.
      const estimated =
        r.success?.exactness === "estimated" || r.failure?.exactness === "estimated";
      const measure = estimated ? "estimate" : "exact";
      return (
        <span className="imcp-num" data-measure={measure} title={`Total operations for ${toolLabel(r.tool)}`}>
          {defaultNumberFormat(total)}
          {estimateSuffix(measure)}
        </span>
      );
    },
  };
}
